import { useEffect, useRef, useState } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { X } from 'lucide-react';

/** Full-screen rear-camera barcode scanner. In `continuous` mode it keeps
 * scanning (ignoring the same code for 1.5s) so receivers can sweep a box. */
export default function BarcodeScanner({ onDetected, onClose, continuous = false }) {
  const videoRef = useRef(null);
  const lastRef = useRef({ code: '', at: 0 });
  const [error, setError] = useState('');
  const [lastCode, setLastCode] = useState('');

  useEffect(() => {
    let controls;
    let cancelled = false;
    const reader = new BrowserMultiFormatReader();
    reader.decodeFromConstraints(
      { video: { facingMode: { ideal: 'environment' } } }, videoRef.current,
      (result, _err, ctrl) => {
        if (!result) return;
        const code = result.getText();
        const now = Date.now();
        if (code === lastRef.current.code && now - lastRef.current.at < 1500) return;
        lastRef.current = { code, at: now };
        if (navigator.vibrate) navigator.vibrate(60);
        setLastCode(code);
        if (!continuous) ctrl.stop();
        onDetected(code);
      },
    ).then((c) => { if (cancelled) c.stop(); else controls = c; })
      .catch(() => setError('Camera unavailable. Allow camera access, or type the number instead.'));
    return () => { cancelled = true; controls?.stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col" data-testid="staff-app-scanner">
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <p className="text-sm font-semibold">{continuous ? 'Scan items — keeps scanning' : 'Point at a barcode'}</p>
        <button onClick={onClose} aria-label="Close scanner" data-testid="staff-app-scanner-close" className="p-2 rounded-full bg-white/10">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="relative flex-1 overflow-hidden">
        <video ref={videoRef} className="absolute inset-0 w-full h-full object-cover" muted playsInline />
        <div className="absolute inset-x-8 top-1/2 -translate-y-1/2 h-40 border-2 border-white/80 rounded-xl staff-app-scan-frame" />
      </div>
      <div className="px-4 py-4 text-center text-sm text-white/90 min-h-[56px]" data-testid="staff-app-scanner-status">
        {error || (lastCode ? `Scanned ${lastCode}` : 'Hold steady inside the frame')}
      </div>
    </div>
  );
}
